import { useTranslation } from '@mukil/i18n';
import { Alert, Button, GlassCard, LoadingSpinner } from '@jumpstone/ui-react';

/**
 * Step 6 — Import: drives the chunked, resumable commit. The screen owns the
 * commit loop (re-invoking `commit` until `committed`); this component only
 * renders the live `{ loaded, total }` progress and, on a stall, a "Continue
 * import" affordance. JSX-only.
 */
export function MigrationImportStep({
  loaded,
  total,
  running,
  stalled,
  hardFailed,
  onContinue,
}: {
  loaded: number;
  total: number;
  running: boolean;
  /** Progress stopped advancing but the run is resumable — offer Continue. */
  stalled: boolean;
  /** The run is terminal-not-success (failed/aborted) — Continue cannot help. */
  hardFailed: boolean;
  onContinue: () => void;
}): React.JSX.Element {
  const { t } = useTranslation('wp');
  const pct = total > 0 ? Math.min(100, Math.round((loaded / total) * 100)) : 0;

  return (
    <GlassCard className="rounded-2xl p-5">
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-3">
          {running && <LoadingSpinner />}
          <span className="text-sm font-medium">
            {t('migration.import.progress', { loaded, total })}
          </span>
        </div>

        <div
          className="h-2 w-full overflow-hidden rounded-full bg-[color:var(--line)]"
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div
            className="h-full rounded-full bg-[color:var(--accent)] transition-[width]"
            style={{ width: `${String(pct)}%` }}
          />
        </div>

        {hardFailed ? (
          <Alert variant="danger" title={t('migration.import.failed_title')}>
            {t('migration.import.failed_body')}
          </Alert>
        ) : stalled ? (
          <Alert variant="warning" title={t('migration.import.stalled_title')}>
            {t('migration.import.stalled_body')}
          </Alert>
        ) : null}

        <p className="text-xs text-[color:var(--ink-muted)]">
          {t('migration.import.safe_to_leave')}
        </p>

        {/* Continue only helps a resumable stall — never a hard failure. */}
        {!running && stalled && !hardFailed && (
          <div className="flex justify-end">
            <Button onClick={onContinue}>
              {t('migration.import.continue')}
            </Button>
          </div>
        )}
      </div>
    </GlassCard>
  );
}
